import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { formatDate } from '@/lib/utils/date';
import {
  useCampaignReportDownload,
  usePromptVoiceCampaigns,
} from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { CampaignRow } from '../types';

type FilterField = 'promptId' | 'campaignId' | 'campaignName';

const FILTER_OPTIONS: { value: FilterField; label: string }[] = [
  { value: 'promptId', label: 'Prompt ID' },
  { value: 'campaignId', label: 'Campaign ID' },
  { value: 'campaignName', label: 'Campaign name' },
];

export function PromptVoiceCampaignsPage() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isAdmin = role === USER_ROLES.ADMIN;
  const search = usePromptVoiceCampaigns();
  const rows = search.data ?? [];

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [filterBy, setFilterBy] = useState<FilterField>('promptId');
  const [filterData, setFilterData] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const reportDownload = useCampaignReportDownload();
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        showOwner: true,
        hideActions: true,
        onDownloadReport: reportDownload.download,
      }),
    [reportDownload.download],
  );

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    userId: true,
    username: true,
  };

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    search.mutate(
      {
        ...(isAdmin ? { userId: Number(userId) } : {}),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
        filterBy,
        filterData: filterData.trim(),
      },
      {
        onError: () => toast.error('Could not fetch prompt voice report.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Prompt Campaigns"
        description="Filter prompt-based voice campaigns by date and identifier."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Combine date range with an optional field-specific filter.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Start date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="End date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-by">Field</Label>
              <Select
                value={filterBy}
                onValueChange={(v) => setFilterBy(v as FilterField)}
              >
                <SelectTrigger id="filter-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-data">Value</Label>
              <Input
                id="filter-data"
                value={filterData}
                onChange={(e) => setFilterData(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <Button onClick={handleSearch} disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:prompt:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={hasSearched ? 'No campaigns match' : 'Run a search to begin'}
        emptyDescription={
          hasSearched
            ? 'Try a wider date range or different filter value.'
            : 'Pick a date range and optional filter, then click Search.'
        }
        getRowId={(row) => String(row.campaignId)}
        initialColumnVisibility={visibility}
        exportFilename="prompt-voice-campaigns.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
